import { IFeatureSettings } from './ISettings';

export interface ISettingsManager<ISettings> {
  get<T extends keyof ISettings>(settingsName: T): ISettings[T];
  getRequired<T extends keyof ISettings>(settingName: T): Exclude<ISettings[T], undefined | null>;
  getAll(): ISettings;
  addSettings(settings: ISettings): ISettingsManager<ISettings>;
}

export function create<ISettings>(settings?: ISettings): ISettingsManager<ISettings> {
  let savedSettings: ISettings = settings || ({} as ISettings);
  const settingsManager = {
    get: <T extends keyof ISettings>(settingName: T): ISettings[T] => {
      return savedSettings[settingName];
    },
    getRequired: <T extends keyof ISettings>(settingName: T): Exclude<ISettings[T], undefined | null> => {
      const value = savedSettings[settingName];

      if (value == null) {
        throw Error(`"${String(settingName)}" is not available in the current environment`);
      }

      return value as Exclude<ISettings[T], undefined | null>;
    },
    getAll: () => {
      return savedSettings;
    },
    addSettings: (newSettings: ISettings): ISettingsManager<ISettings> => {
      savedSettings = {
        ...savedSettings,
        ...newSettings,
      };
      return settingsManager;
    },
  };

  return settingsManager;
}

export const isFeatureEnabled = (
  featureSettings: IFeatureSettings = { enabled: false },
  tenantId?: string,
): boolean => {
  // If the global enabled flag is false, no need to check further
  if (!featureSettings.enabled) {
    return false;
  }

  // If no specific tenant management, return the global enabled status
  if (!featureSettings.enabledTenants && !featureSettings.excludedTenants) {
    return true;
  }

  // If tenantId is not provided when it's required, return false
  if (!tenantId) {
    console.error('Tenant id is not provided');
    return false;
  }

  // Check tenant-specific settings
  if (featureSettings.excludedTenants && featureSettings.excludedTenants.includes(tenantId)) {
    return false;
  }
  if (featureSettings.enabledTenants && !featureSettings.enabledTenants.includes(tenantId)) {
    return false;
  }

  // If none of the disqualifying conditions are met, the feature is enabled for the tenant
  return true;
};
